import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import AdminLogin from './Pages/AdminLogin';
import AdminDashboard from './Pages/AdminDashboard';
import AdminAudit from './Pages/AdminAudit';
import ThemeManager from './Pages/ThemeManager';
import AdminShipments from './Pages/AdminShipments';
import AdminOrderReports from './Pages/AdminOrderReports';
import AdminOnlineSalesReport from './Pages/AdminOnlineSalesReport';
import AdminLabelPrint from './Pages/AdminLabelPrint';
import GeneralSettings from './Pages/GeneralSettings';
import AdminDiscounts from './Pages/AdminDiscounts';
import AdminReviews from './Pages/AdminReviews';
import AdminLocations from './Pages/AdminLocations';
import AdminCategories from './Pages/AdminCategories';
import MenuImportPage from './Pages/MenuImportPage';
import AdminPos from './Pages/AdminPos';
import AdminPosCallback from './Pages/AdminPosCallback';
import AdminPosQueue from './Pages/AdminPosQueue';
import AdminOrdersQueue from './Pages/AdminOrdersQueue';
import PosTakePaymentReturn from './Pages/PosTakePaymentReturn';
import AdminUserManagement from './Pages/AdminUserManagement';
import { resolvePageBackground } from './utils/themeBackground';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

/**
 * Applies the tenant's storefront theme background color to the admin chrome.
 * Reads the saved theme from GET /admin/theme and resolves the page background
 * the same way the storefront does (auto = tint of menuColor, custom =
 * pageBackgroundColor), then sets it as the --admin-page-bg CSS variable so it
 * is NOT hardcoded and matches whatever the tenant configured in Theme settings.
 */
function ThemeBackground() {
  const location = useLocation();
  const loadedRef = React.useRef(false);

  const pathname = String(location?.pathname || '');
  const isAdminRoute = pathname.startsWith('/admin') && pathname !== '/admin/login';

  useEffect(() => {
    if (!isAdminRoute || loadedRef.current) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        if (!API_BASE_URL) return;
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const res = await fetch(`${API_BASE_URL}/admin/theme`, {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || cancelled) return;

        const bg = resolvePageBackground(data?.theme || {});
        if (bg) {
          document.documentElement.style.setProperty('--admin-page-bg', bg);
          document.body.style.backgroundColor = bg;
          loadedRef.current = true; // only cache on success so we can retry post-login
        }
      } catch {
        // Not authenticated yet / theme unavailable — keep fallback, retry later.
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isAdminRoute, pathname]);

  return null;
}

function ScrollToTopButton() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const pathname = String(location?.pathname || '');
  const isAdminRoute = pathname.startsWith('/admin') && pathname !== '/admin/login';

  useEffect(() => {
    if (!isAdminRoute) {
      setVisible(false);
      return undefined;
    }

    const onScroll = () => {
      setVisible(window.scrollY > 450);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isAdminRoute, pathname]);

  if (!isAdminRoute || !visible) return null;

  return (
    <button
      type="button"
      className="scroll-top-btn"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      title="Scroll to top"
    >
      <i className="fas fa-arrow-up" aria-hidden="true"></i>
    </button>
  );
}

function App() {
  const enablePos = String(process.env.REACT_APP_ENABLE_POS || '').toLowerCase() === 'true';
  const [storeMode, setStoreMode] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        if (!API_BASE_URL) return;
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const res = await fetch(`${API_BASE_URL}/admin/general`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;
        const rawMode = data?.settings?.storeMode ?? data?.storeMode ?? '';
        setStoreMode(String(rawMode || '').trim().toUpperCase());
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  const posEnabled = useMemo(() => {
    if (enablePos) return true;
    const mode = String(storeMode || '').trim().toUpperCase();
    return mode === 'POS' || mode === 'HYBRID';
  }, [enablePos, storeMode]);

  return (
    <BrowserRouter>
      <div className="App">
        <ThemeBackground />
        <ScrollToTopButton />
        <Routes>
          {/* Redirect bare root to admin login */}
          <Route path="/" element={<Navigate to="/admin/login" replace />} />

          <Route path="/pos/take-payment/return" element={<PosTakePaymentReturn />} />

          {/* Keep old non-prefixed paths working by redirecting them */}
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />

          {/* Primary admin routes used in production */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/general" element={<GeneralSettings />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/discounts" element={<AdminDiscounts />} />
          <Route path="/admin/locations" element={<AdminLocations />} />
          <Route path="/admin/categories" element={<AdminCategories />} />
          <Route path="/admin/menu-import" element={<MenuImportPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/fulfillment" element={<Navigate to="/admin/reports/fulfillment" replace />} />
          <Route path="/admin/audit" element={<AdminAudit />} />
          <Route path="/admin/theme" element={<ThemeManager />} />
          <Route path="/admin/shipments" element={<AdminShipments />} />
          <Route path="/admin/labels/print" element={<AdminLabelPrint />} />
          <Route path="/admin/reports/orders" element={<AdminOrderReports />} />
          <Route path="/admin/reports/fulfillment" element={<AdminOrderReports />} />
          <Route path="/admin/reports/online-sales" element={<AdminOnlineSalesReport />} />

          <Route path="/admin/channels/orders/queue" element={<AdminOrdersQueue />} />

          {/* User Management (Superadmin-only; gated in-page + server-side) */}
          <Route path="/admin/users" element={<AdminUserManagement />} />

          {posEnabled ? <Route path="/admin/channels/pos" element={<AdminPos />} /> : null}
          {posEnabled ? <Route path="/admin/channels/pos/queue" element={<AdminPosQueue />} /> : null}
          {posEnabled ? <Route path="/admin/channels/pos/callback" element={<AdminPosCallback />} /> : null}
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
